import React, { useEffect, useRef, useState } from 'react';
import { Button, Drawer, Modal, Space, message } from 'antd';
import { CameraOutlined, PictureOutlined } from '@ant-design/icons';

function isSecureCameraContext() {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext === true;
}

function isLikelyMobile() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  return Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
}

/**
 * 底部选择：拍照 / 相册。
 * 手机端优先用带 capture 的系统文件选择（直接进相机）；
 * 仅在 HTTPS 桌面环境才尝试 getUserMedia 预览。
 */
export default function AssetPhotoPicker({
  open,
  onClose,
  onPicked,
  disabled = false,
}) {
  const albumInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);

  const stopLive = () => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => () => stopLive(), []);

  const handleFile = async (file) => {
    if (!file || disabled) return;
    try {
      await onPicked?.(file);
    } catch (err) {
      message.error(err?.message || '处理图片失败');
    }
  };

  /** 每次临时创建带 capture 的 input，避免部分机型缓存成「只开相册」 */
  const openSystemCamera = () => {
    if (disabled) return;
    onClose?.();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // environment=后置；部分安卓对布尔 capture 更敏感
    input.setAttribute('capture', 'environment');
    input.style.display = 'none';
    const cleanup = () => {
      input.onchange = null;
      input.remove();
    };
    input.onchange = () => {
      const f = input.files?.[0];
      cleanup();
      if (f) handleFile(f);
    };
    document.body.appendChild(input);
    // 同一用户手势栈内点击；Drawer 关闭后再点，部分 WebView 会丢 capture
    try {
      input.click();
    } catch {
      window.setTimeout(() => {
        try {
          input.click();
        } catch {
          cleanup();
          message.error('无法打开相机，请改用「从相册选择」或使用系统浏览器打开本站');
        }
      }, 50);
    }
    // 用户取消时不会触发 onchange，延时回收节点
    window.setTimeout(cleanup, 120000);
  };

  const openAlbum = () => {
    if (disabled) return;
    onClose?.();
    window.setTimeout(() => albumInputRef.current?.click(), 200);
  };

  const openLiveCamera = async () => {
    // 手机 / 非 HTTPS：不要走 getUserMedia（会失败或表现怪异），直接系统相机
    if (isLikelyMobile() || !isSecureCameraContext() || !navigator.mediaDevices?.getUserMedia) {
      openSystemCamera();
      return;
    }
    onClose?.();
    try {
      setLiveBusy(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setLiveOpen(true);
      window.setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play?.().catch(() => {});
        }
      }, 50);
    } catch {
      message.info('无法打开内置摄像头，已切换为系统拍照');
      openSystemCamera();
    } finally {
      setLiveBusy(false);
    }
  };

  const captureLive = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      message.warning('摄像头尚未就绪');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    stopLive();
    setLiveOpen(false);
    if (!blob) {
      message.error('拍照失败');
      return;
    }
    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
    await handleFile(file);
  };

  return (
    <>
      <input
        ref={albumInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          handleFile(f);
        }}
      />
      <Drawer
        title="上传资产照片"
        placement="bottom"
        height={240}
        open={open}
        onClose={onClose}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Button
            type="primary"
            block
            icon={<CameraOutlined />}
            disabled={disabled || liveBusy}
            loading={liveBusy}
            onClick={openLiveCamera}
          >
            拍照上传
          </Button>
          <Button block icon={<PictureOutlined />} disabled={disabled} onClick={openAlbum}>
            从相册选择
          </Button>
          <div style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'center', lineHeight: 1.5 }}>
            手机请允许打开相机；若仍进相册，请用系统浏览器（Safari/Chrome）访问，勿用部分 App 内置浏览器
          </div>
        </Space>
      </Drawer>
      <Modal
        title="拍照"
        open={liveOpen}
        onCancel={() => {
          stopLive();
          setLiveOpen(false);
        }}
        footer={[
          <Button
            key="sys"
            onClick={() => {
              stopLive();
              setLiveOpen(false);
              openSystemCamera();
            }}
          >
            改用系统相机
          </Button>,
          <Button key="shot" type="primary" onClick={captureLive}>
            拍摄
          </Button>,
        ]}
        destroyOnClose
        width={420}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: '100%', borderRadius: 8, background: '#000', maxHeight: 360 }}
        />
      </Modal>
    </>
  );
}
